import { z } from 'zod';
import { trainingCommandIdSchema, trainingContentKeySchema, trainingCourseIdSchema, trainingReleaseIdSchema, } from './training-content.contract.js';
export const trainingActivityCapability = 'training.activity.v1';
export const trainingActivitySummaryCapability = 'training.activity-summary.v1';
export const trainingActivitySessionIdSchema = z.string().uuid();
export const trainingActivityTypeSchema = z.enum(['catalog', 'lesson', 'assessment', 'assessment-preview']);
export const trainingStartActivitySchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    sessionId: trainingActivitySessionIdSchema,
    courseId: trainingCourseIdSchema,
    releaseId: trainingReleaseIdSchema,
    lessonKey: trainingContentKeySchema.nullable().default(null),
    activityType: trainingActivityTypeSchema,
});
export const trainingHeartbeatActivitySchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    active: z.boolean(),
    interactionDelta: z.number().int().min(0).max(1_000),
});
export const trainingCloseActivitySchema = z.strictObject({
    commandId: trainingCommandIdSchema,
    expectedRevision: z.number().int().positive(),
});
export const trainingActivitySessionPublicSchema = z.strictObject({
    id: trainingActivitySessionIdSchema,
    courseId: trainingCourseIdSchema,
    releaseId: trainingReleaseIdSchema,
    lessonKey: trainingContentKeySchema.nullable(),
    activityType: trainingActivityTypeSchema,
    status: z.enum(['open', 'closed']),
    revision: z.number().int().positive(),
    startedAt: z.string().datetime({ offset: true }),
    lastHeartbeatAt: z.string().datetime({ offset: true }),
    closedAt: z.string().datetime({ offset: true }).nullable(),
});
export const trainingActivityPrivateSchema = trainingActivitySessionPublicSchema.extend({
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    activeSeconds: z.number().int().nonnegative(),
    interactionCount: z.number().int().nonnegative(),
    heartbeatCount: z.number().int().nonnegative(),
});
export const trainingActivityQuerySchema = z.strictObject({
    membershipId: z.string().uuid().nullable().default(null),
    courseId: trainingCourseIdSchema.nullable().default(null),
    from: z.string().datetime({ offset: true }).nullable().default(null),
    to: z.string().datetime({ offset: true }).nullable().default(null),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().min(0).default(0),
});
export const trainingActivityResponseSchema = z.strictObject({
    data: trainingActivitySessionPublicSchema,
    command: z.strictObject({ commandId: trainingCommandIdSchema, idempotent: z.boolean() }),
});
export const trainingPrivateActivityResponseSchema = z.strictObject({
    data: z.array(trainingActivityPrivateSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const trainingActivitySummaryQuerySchema = z.strictObject({
    membershipId: z.string().uuid(),
    fromInclusive: z.string().datetime({ offset: true }),
    toExclusive: z.string().datetime({ offset: true }),
    limit: z.number().int().min(1).max(100).default(50),
}).superRefine((value, context) => {
    const from = Date.parse(value.fromInclusive);
    const to = Date.parse(value.toExclusive);
    if (to <= from) {
        context.addIssue({
            code: 'custom',
            path: ['toExclusive'],
            message: 'The Training activity summary end must follow its start.',
        });
    }
    if (to - from > 366 * 86_400_000) {
        context.addIssue({
            code: 'custom',
            path: ['toExclusive'],
            message: 'A Training activity summary cannot span more than 366 days.',
        });
    }
});
export const trainingActivitySummaryLifecycleSchema = z.enum([
    'live',
    'no-recent-heartbeat',
    'interrupted',
    'closed',
]);
export const trainingActivitySummaryExpectationSourceSchema = z.enum([
    'authored-course-estimate',
    'authored-lesson-estimate',
]);
export const trainingActivitySummarySessionSchema = z.strictObject({
    id: trainingActivitySessionIdSchema,
    courseId: trainingCourseIdSchema,
    releaseId: trainingReleaseIdSchema,
    lessonKey: trainingContentKeySchema.nullable(),
    activityType: trainingActivityTypeSchema,
    lifecycle: trainingActivitySummaryLifecycleSchema,
    courseTitle: z.string().trim().min(1).max(250),
    pageTitle: z.string().trim().min(1).max(250),
    startedAt: z.string().datetime({ offset: true }),
    lastHeartbeatAt: z.string().datetime({ offset: true }),
    closedAt: z.string().datetime({ offset: true }).nullable(),
    pageOpenSeconds: z.number().int().nonnegative(),
    activeSeconds: z.number().int().nonnegative(),
    interactionCount: z.number().int().nonnegative(),
    expectedSeconds: z.number().int().positive().nullable(),
    expectationSource: trainingActivitySummaryExpectationSourceSchema.nullable(),
});
export const trainingActivitySummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(trainingActivitySummaryCapability),
        factKind: z.literal('supporting-training-activity'),
        payableTime: z.literal(false),
        membershipId: z.string().uuid(),
        range: z.strictObject({
            fromInclusive: z.string().datetime({ offset: true }),
            toExclusive: z.string().datetime({ offset: true }),
        }),
        totals: z.strictObject({
            visitCount: z.number().int().nonnegative(),
            pageOpenSeconds: z.number().int().nonnegative(),
            activeSeconds: z.number().int().nonnegative(),
            interactionCount: z.number().int().nonnegative(),
        }),
        sessions: z.array(trainingActivitySummarySessionSchema).max(100),
    }),
    page: z.strictObject({
        limit: z.number().int().positive(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=training-activity.contract.js.map